import React, { useEffect, useMemo, useRef } from 'react';
import { diffWords, type Change } from 'diff';
import { InlineMarkdownText } from './InlineMarkdownText';
import { hasInlineMarkdownMarkers, stripInlineMarkdown } from '../utils/inline-markdown';

type TextDiffProps = {
  before: string;
  after?: string;
  variant: 'replacement' | 'deletion' | 'insertion' | 'inline';
  beforeLabel?: string;
  afterLabel?: string;
  deleteLabel?: string;
  insertLabel?: string;
  whitespaceLabel?: string;
  className?: string;
  activeChangeIndex?: number | null;
  scrollRequestKey?: number;
};

export const isWhitespaceOnlyText = (value: string): boolean =>
  value !== '' && value.trim() === '';

const renderPossiblyWhitespaceText = (
  value: string,
  whitespaceLabel: string,
): React.ReactNode => {
  if (!isWhitespaceOnlyText(value)) {
    return <InlineMarkdownText text={value} />;
  }
  return (
    <span style={{ fontFamily: "'DM Mono', monospace" }}>{whitespaceLabel}</span>
  );
};

const renderFormattedText = (value: string): React.ReactNode => (
  <InlineMarkdownText text={value} />
);

export const DiffRemovedSpan: React.FC<{
  children: React.ReactNode;
  changeIndex?: number;
  isActive?: boolean;
}> = ({ children, changeIndex, isActive = false }) => (
  <span
    data-diff-change={changeIndex !== undefined ? String(changeIndex) : undefined}
    className={`bg-red-500/30 text-red-900 dark:text-red-100 line-through ${
      isActive ? 'ring-2 ring-primary ring-offset-1 rounded-sm' : ''
    }`.trim()}
  >
    {children}
  </span>
);

export const DiffAddedSpan: React.FC<{
  children: React.ReactNode;
  changeIndex?: number;
  isActive?: boolean;
}> = ({ children, changeIndex, isActive = false }) => (
  <span
    data-diff-change={changeIndex !== undefined ? String(changeIndex) : undefined}
    className={`bg-success/30 text-success dark:text-success ${
      isActive ? 'ring-2 ring-primary ring-offset-1 rounded-sm' : ''
    }`.trim()}
  >
    {children}
  </span>
);

const renderBeforeRow = (parts: Change[]): React.ReactNode =>
  parts.map((part, index) => {
    if (part.added) {
      return null;
    }
    if (part.removed) {
      return <DiffRemovedSpan key={index}>{part.value}</DiffRemovedSpan>;
    }
    return <span key={index}>{part.value}</span>;
  });

const renderAfterRow = (parts: Change[]): React.ReactNode =>
  parts.map((part, index) => {
    if (part.removed) {
      return null;
    }
    if (part.added) {
      return <DiffAddedSpan key={index}>{part.value}</DiffAddedSpan>;
    }
    return <span key={index}>{part.value}</span>;
  });

const renderInline = (
  parts: Change[],
  activeChangeIndex: number | null | undefined,
): React.ReactNode => {
  let changeIndex = 0;
  return parts.map((part, index) => {
    if (part.added) {
      const current = changeIndex;
      changeIndex += 1;
      return (
        <DiffAddedSpan
          key={index}
          changeIndex={current}
          isActive={activeChangeIndex === current}
        >
          {part.value}
        </DiffAddedSpan>
      );
    }
    if (part.removed) {
      const current = changeIndex;
      changeIndex += 1;
      return (
        <DiffRemovedSpan
          key={index}
          changeIndex={current}
          isActive={activeChangeIndex === current}
        >
          {part.value}
        </DiffRemovedSpan>
      );
    }
    return <span key={index}>{part.value}</span>;
  });
};

export const countInlineDiffChanges = (before: string, after: string): number => {
  return diffWords(stripInlineMarkdown(before), stripInlineMarkdown(after))
    .filter((part) => part.added || part.removed).length;
};

export const TextDiff: React.FC<TextDiffProps> = ({
  before,
  after = '',
  variant,
  beforeLabel,
  afterLabel,
  deleteLabel,
  insertLabel,
  whitespaceLabel = '[whitespace]',
  className = '',
  activeChangeIndex = null,
  scrollRequestKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMarkdown = hasInlineMarkdownMarkers(before) || hasInlineMarkdownMarkers(after);
  const beforePlain = stripInlineMarkdown(before);
  const afterPlain = stripInlineMarkdown(after);
  const isFormatOnlyChange = hasMarkdown && beforePlain === afterPlain && before !== after;

  const diffParts = useMemo(
    () => {
      if (variant === 'deletion' || variant === 'insertion') {
        return [];
      }
      if (hasMarkdown) {
        return diffWords(beforePlain, afterPlain);
      }
      return diffWords(before, after);
    },
    [before, after, beforePlain, afterPlain, hasMarkdown, variant],
  );

  useEffect(() => {
    if (variant !== 'inline' || activeChangeIndex === null || activeChangeIndex === undefined) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const target = container.querySelector(
      `[data-diff-change="${activeChangeIndex}"]`,
    ) as HTMLElement | null;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeChangeIndex, scrollRequestKey, variant, before, after]);

  return (
    <div
      ref={containerRef}
      className={`mt-4 rounded-md border bg-muted/30 p-4 text-left ${
        variant === 'inline' ? 'overflow-y-auto max-h-[50vh]' : ''
      } ${className}`.trim()}
    >
      {variant === 'deletion' ? (
        <div>
          <div className="text-xs text-muted-foreground mb-1 uppercase">{deleteLabel}</div>
          <div className="text-[0.9rem] whitespace-pre-wrap">
            <DiffRemovedSpan>{renderFormattedText(before)}</DiffRemovedSpan>
          </div>
        </div>
      ) : variant === 'insertion' ? (
        <div>
          <div className="text-xs text-muted-foreground mb-1 uppercase">{insertLabel}</div>
          <div className="text-[0.9rem] whitespace-pre-wrap">
            <DiffAddedSpan>{renderPossiblyWhitespaceText(after, whitespaceLabel)}</DiffAddedSpan>
          </div>
        </div>
      ) : variant === 'inline' ? (
        <div className="font-mono text-sm whitespace-pre-wrap">
          {isFormatOnlyChange ? (
            <>
              <DiffRemovedSpan changeIndex={0} isActive={activeChangeIndex === 0}>
                {renderFormattedText(before)}
              </DiffRemovedSpan>
              <DiffAddedSpan changeIndex={1} isActive={activeChangeIndex === 1}>
                {renderFormattedText(after)}
              </DiffAddedSpan>
            </>
          ) : (
            renderInline(diffParts, activeChangeIndex)
          )}
        </div>
      ) : hasMarkdown ? (
        <div className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1 uppercase">{beforeLabel}</div>
            <div className="text-[0.9rem] whitespace-pre-wrap">
              {isFormatOnlyChange
                ? renderFormattedText(before)
                : renderBeforeRow(diffParts)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 uppercase">{afterLabel}</div>
            <div className="text-[0.9rem] whitespace-pre-wrap">
              {isFormatOnlyChange || hasInlineMarkdownMarkers(after) ? (
                <DiffAddedSpan>{renderFormattedText(after)}</DiffAddedSpan>
              ) : (
                renderAfterRow(diffParts)
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1 uppercase">{beforeLabel}</div>
            <div className="text-[0.9rem] whitespace-pre-wrap">{renderBeforeRow(diffParts)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1 uppercase">{afterLabel}</div>
            <div className="text-[0.9rem] whitespace-pre-wrap">{renderAfterRow(diffParts)}</div>
          </div>
        </div>
      )}
    </div>
  );
};
